import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MD_DIALOG_DATA, MdDialog, MdDialogRef } from '@angular/material';
import { DialogAlertComponent } from 'app/components/modal/dialog-alert/dialog-alert.component';
import { CartInfoModel } from 'app/model/checkout/cart-info-model';
import { PaymentChannelCheckout } from 'app/model/checkout/checkout3-config.model';
import { PaymentMethodDetailType } from 'app/model/constant';
import { AppConfigService } from 'app/services/app-config/app-config.service';
import { CheckoutService } from 'app/services/checkout/checkout.service';
import { PromotionCodeDiscountType } from 'app/services/promotion-code/enum/promotion-code.enum';
import { PromotionCodeActiveListRequest, PromotionCodeActiveListResponse, PromotionCodeDTO, PromotionCodePaymentMethodResponse, PromotionCodeResponse } from 'app/services/promotion-code/model/promotion-code.model';
import { PromotionCodeService } from 'app/services/promotion-code/promotion-code.service';
import { UtilitiesService } from 'app/services/utilities/utilities.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'act-dialog-use-promotion-code',
  templateUrl: './dialog-use-promotion-code.component.html',
  styleUrls: ['./dialog-use-promotion-code.component.scss']
})
export class DialogUsePromotionCodeComponent implements OnInit, OnDestroy {
  public elementId: string = 'dialog-use-promo-code';
  public isLoading: boolean = false;
  public isMobile: boolean = false;
  public searchControl: FormControl = new FormControl('');
  public searchErrorMessage: string = '';
  public promotionCodeActiveList: PromotionCodeDTO[] = [];
  public promotionCodeActiveDisplayList: PromotionCodeDTO[] = [];
  public selectedItem: PromotionCodeDTO;

  private cart: CartInfoModel;
  private currentPromotionCode: PromotionCodeResponse;
  private searchSubscription: Subscription;

  constructor(
    @Inject(MD_DIALOG_DATA) private data,
    private dialog: MdDialog,
    public dialogRef: MdDialogRef<DialogUsePromotionCodeComponent>,
    private utilitiesService: UtilitiesService,
    private appConfigService: AppConfigService,
    private checkoutService: CheckoutService,
    private promotionCodeService: PromotionCodeService,
  ) {}

  ngOnInit() {
    this.isMobile = this.utilitiesService.isMobile();
    this.cart = this.checkoutService.getCheckoutData().cart;
    this.currentPromotionCode = this.checkoutService.getCurrentPromotionCode().codeInfo;
    this.subscribeSearchChange();
    this.getPromotionCodeActiveList(this.currentPromotionCode ? this.currentPromotionCode.promotionCode : undefined);
  }

  ngOnDestroy() {
    this.utilitiesService.unlockBody();
    this.utilitiesService.unlockBodyBackdrop();
    this.searchSubscription && this.searchSubscription.unsubscribe();
  }

  subscribeSearchChange() {
    this.searchSubscription = this.searchControl.valueChanges.subscribe(value => {
      const upper = value.toUpperCase();

      if (value !== upper) {
        this.searchControl.setValue(upper, { emitEvent: false });
      }

      this.searchErrorMessage = '';
    });
  }

  generatePromotionCodeActiveListRequest(): PromotionCodeActiveListRequest {
    const currentSelectedPromotionCode = this.currentPromotionCode ? this.currentPromotionCode.promotionCode : '';
    const promotionCode = this.searchControl.value ? this.searchControl.value.trim() : currentSelectedPromotionCode;
    return {
      promotionCode: promotionCode,
      searchId: this.cart.searchId,
      lineItems: this.cart.lineItems
    };
  }

  getPromotionCodeActiveList(code?: string) {
    this.isLoading = true;
    this.promotionCodeService.getPromotionCodeActiveList(this.generatePromotionCodeActiveListRequest())
      .subscribe(
        (res: PromotionCodeActiveListResponse) => {
          this.promotionCodeActiveList = [];
          this.promotionCodeActiveDisplayList = [];
          if (res) {
            this.transformPromotionCodeResponse(res.promotionCodes);
            if (code) {
              this.handleApplyCodeAfterSearch(code);
            }
          }
          this.isLoading = false;
        },
        (errors) => {
          this.isLoading = false;
          this.handleErrorPromotionCodeActiveList(errors);
        }
      );
  }

  handleErrorPromotionCodeActiveList(errors: any) {
    const errorBody = errors.json();
    if (errorBody && errorBody.errors && errorBody.errors.length) {
      const errorCode = errorBody.errors[0].code;
      if (errorCode === '4723') {
        const dialogRef = this.dialog.open(DialogAlertComponent);
        dialogRef.componentInstance.alertHeader = this.appConfigService.getTranslate('cart-dialog-error-session-expired-title');
        dialogRef.componentInstance.alertText = this.appConfigService.getTranslate('cart-dialog-error-session-expired-description');
        dialogRef.componentInstance.imageSrc = '/assets/images/icons/Error.png';
        dialogRef.componentInstance.imageSrcSet = '/assets/images/icons/Error.png';
        dialogRef.afterClosed().subscribe(() => {
          this.handleCloseDialog('error', errorCode);
        });
      } else {
        const dialogRef: MdDialogRef<DialogAlertComponent> = this.dialog.open(DialogAlertComponent);
        dialogRef.componentInstance.alertText = this.appConfigService.getTranslate('system-error');
        dialogRef.componentInstance.imageSrc = '/assets/images/icons/icon-failed.png';
        dialogRef.componentInstance.imageSrcSet = '/assets/images/icons/icon-failed.png';
        dialogRef.afterClosed().subscribe(() => {
          this.handleCloseDialog('close');
        });
      }
    }
  }

  transformPromotionCodeResponse(promotionCodes: PromotionCodeResponse[]) {
    const promotionCodesDisplay = promotionCodes.map((item, index) => {

      const discountType = item.discount.discountType;
      let discount = PromotionCodeDiscountType.AMOUNT == discountType ? this.utilitiesService.commaSeparateNumber(item.discount.discount) : item.discount.discount;
      const discountDisplay = this.appConfigService.getTranslate('cart-dialog-use-promotion-code-discount')
          .replace('{amount}', PromotionCodeDiscountType.AMOUNT == discountType ? `THB ${discount}` : `${discount}%`);

      const discountAmountDisplay = this.utilitiesService.commaSeparateNumber(item.discount.discountAmount);

      const maxDiscountPerBook = item.discount.maxDiscountPerBook ? this.utilitiesService.commaSeparateNumber(item.discount.maxDiscountPerBook) : undefined;
      const maxDiscountPerBookDisplay = maxDiscountPerBook
          ? this.appConfigService.getTranslate('cart-dialog-use-promotion-code-max-discount-per-book').replace('{amount}', `THB ${maxDiscountPerBook}`)
          : '';

      const minSpendPerBook = item.discount.minSpendPerBook ? this.utilitiesService.commaSeparateNumber(item.discount.minSpendPerBook) : undefined;
      const minSpendPerBookDisplay = minSpendPerBook
          ? this.appConfigService.getTranslate('cart-dialog-use-promotion-code-min-spend-per-book').replace('{amount}', `THB ${minSpendPerBook}`)
          : this.appConfigService.getTranslate('cart-dialog-use-promotion-code-min-spend-per-book-no-limit');

      return {
        ...item,
        discountDisplay,
        discountAmountDisplay,
        maxDiscountPerBookDisplay,
        minSpendPerBookDisplay,
        paymentMethodDisplay: this.transformPaymentMethodDisplay(item.paymentMethods),
      };
    });

    this.promotionCodeActiveList = promotionCodesDisplay;
    this.promotionCodeActiveDisplayList = [...promotionCodesDisplay];
  }

  transformPaymentMethodDisplay(paymentMethods: PromotionCodePaymentMethodResponse[]): string {
    if (!paymentMethods.length) {
      return '';
    }

    const paymentChannel = new PaymentChannelCheckout();

    if (paymentMethods.length > 1) {
      return this.handleMultiplePaymentMethods(paymentMethods, paymentChannel);
    }

    return this.handleSinglePaymentMethod(paymentMethods[0], paymentChannel);
  }

  handleMultiplePaymentMethods(paymentMethods: PromotionCodePaymentMethodResponse[], paymentChannel: PaymentChannelCheckout): string {
    let paymentMethodDisplay = '';
    const types = paymentMethods.map(p => p.paymentMethod);

    const isAllCardType = types.every(type =>
      type === PaymentMethodDetailType.CREDIT_CARD ||
      type === PaymentMethodDetailType.DEBIT_CARD
    );

    const isAllFleetType = types.every(type =>
      type === PaymentMethodDetailType.CORPORATE_FLEET ||
      type === PaymentMethodDetailType.PERSONAL_FLEET
    );

    if (isAllCardType) {
      paymentMethodDisplay = this.handleCreditDebitCard(paymentMethods[0], paymentChannel, true);
    } else if (isAllFleetType) {
      let paymentMethodTitle = this.appConfigService.getTranslate(paymentChannel.FLEET_CARD.title);
      paymentMethodDisplay = this.appConfigService.getTranslate('cart-dialog-use-promotion-payment').replace('{payment}', paymentMethodTitle);
    } else {
      paymentMethodDisplay = this.appConfigService.getTranslate('cart-dialog-use-promotion-promotional-code-participating-payment-methods');
    }

    return paymentMethodDisplay;
  }

  handleSinglePaymentMethod(payment: PromotionCodePaymentMethodResponse, paymentChannel: PaymentChannelCheckout): string {
    if (payment.paymentMethod === PaymentMethodDetailType.CREDIT_TERMS) {
      return this.appConfigService.getTranslate('cart-dialog-use-promotion-credit-terms');
    }

    if (payment.paymentMethod === PaymentMethodDetailType.CREDIT_CARD || payment.paymentMethod === PaymentMethodDetailType.DEBIT_CARD) {
      return this.handleCreditDebitCard(payment, paymentChannel, false);
    }

    let paymentMethodMap = {};
    paymentMethodMap[PaymentMethodDetailType.TRUE_MONEY] = paymentChannel.TRUE_MONEY_WALLET;
    paymentMethodMap[PaymentMethodDetailType.CORPORATE_FLEET] = paymentChannel.CORPORATE_FLEET;
    paymentMethodMap[PaymentMethodDetailType.PERSONAL_FLEET] = paymentChannel.PERSONAL_FLEET;
    paymentMethodMap[PaymentMethodDetailType.QR_CODE] = paymentChannel.QR_CODE;
    paymentMethodMap[PaymentMethodDetailType.IM_BANKING] = paymentChannel.IM_BANKING;
    paymentMethodMap[PaymentMethodDetailType.PAY_AT_COUNTER] = paymentChannel.PAY_AT_COUNTER;
    paymentMethodMap[PaymentMethodDetailType.E_WALLET] = paymentChannel.E_WALLET;

    var paymentMethodChannel = paymentMethodMap[payment.paymentMethod];

    if (!paymentMethodChannel) {
      return '';
    }

    let title = this.appConfigService.getTranslate(paymentMethodChannel.title);
    let needSpace = payment.paymentMethod === PaymentMethodDetailType.E_WALLET || payment.paymentMethod === PaymentMethodDetailType.QR_CODE;

    return this.appConfigService.getTranslate('cart-dialog-use-promotion-payment').replace('{payment}', `${needSpace ? ' ' : ''}${title}`);
  }

  handleCreditDebitCard(payment: PromotionCodePaymentMethodResponse, paymentChannel: PaymentChannelCheckout, isMultiple: boolean,): string {
    const banks = payment.bank;
    const hasSingleBank = banks && banks.length === 1;
    const hasMultipleBanks = banks && banks.length > 1;

    if (hasMultipleBanks) {
      return this.appConfigService.getTranslate('cart-dialog-use-promotion-promotional-code-participating-payment-methods');
    }

    if (isMultiple) {
      if (hasSingleBank) {
        return this.appConfigService.getTranslate('cart-dialog-use-promotion-credit-debit-card-bank').replace('{bank}', banks[0]);
      }

      let title = this.appConfigService.getTranslate(paymentChannel.CREDIT_CARD.title);
      return this.appConfigService.getTranslate('cart-dialog-use-promotion-payment').replace('{payment}', title);
    }

    let key = payment.paymentMethod === PaymentMethodDetailType.CREDIT_CARD
      ? 'cart-dialog-use-promotion-credit-card'
      : 'cart-dialog-use-promotion-debit-card';

    return this.appConfigService.getTranslate(key).replace('{bank}', hasSingleBank ? `${banks[0]} ` : '');
  }

  onApplyCode() {
    const search = this.searchControl.value ? this.searchControl.value.trim() : '';

    if (!search) {
      return;
    }

    this.getPromotionCodeActiveList(search);
  }

  handleApplyCodeAfterSearch(code: string) {
    const search = code ? code.trim() : '';
    const promotionCode = this.promotionCodeActiveDisplayList.find(
      p => p.promotionCode === search
    );

    if (!promotionCode) {
      this.searchErrorMessage = this.appConfigService.getTranslate('cart-dialog-use-promotion-code-search-error-invalid');
      this.selectedItem = undefined;
      return;
    }

    this.searchErrorMessage = '';
    this.selectedItem = promotionCode;
  }

  resetApplyCode() {
    this.searchControl.setValue('');
    this.selectedItem = undefined;
    this.promotionCodeActiveDisplayList.forEach(i => i.selected = false);
    this.getPromotionCodeActiveList();
  }

  onSelected(item: PromotionCodeDTO) {
    if (item.selected) {
      item.selected = false;
      this.selectedItem = undefined;
      return;
    }

    this.promotionCodeActiveDisplayList.forEach(i => i.selected = false);
    item.selected = true;
    this.selectedItem = item;
  }

  handleSubmit() {
    if (!this.selectedItem) {
      this.handleCloseDialog('submit', {});
      return;
    }

    const {
      discountDisplay,
      discountAmountDisplay,
      maxDiscountPerBookDisplay,
      minSpendPerBookDisplay,
      paymentMethodDisplay,
      selected,
      ...promotionCodeResponse
    } = this.selectedItem;

    this.handleCloseDialog('submit', promotionCodeResponse);
  }

  handleCloseDialog(type: 'close' | 'submit' | 'error', data?: any) {
    this.dialogRef.close({ type, data });
  }

}



// WEBPACK FOOTER //
// ./src/app/components/cart/dialog-use-promotion-code/dialog-use-promotion-code.component.ts